import { fg } from '@atlaskit/platform-feature-flags';

import type { InteractionType, SegmentInfo } from '../../../common';
import { getConfig } from '../../../config';
import type { LabelStack, SegmentLabel } from '../../../interaction-context';
import { type UFOSegmentType } from '../../../segment/segment';
import type { getReactUFOPayloadVersion } from '../../utils/get-react-ufo-payload-version';

import type { LabelStackRegistry } from './label-stack-registry';

export type SegmentItem = {
	n: string;
	c?: Record<string, SegmentItem>;
	t?: UFOSegmentType;
};

export type SegmentTree = {
	r: SegmentItem;
};

export function sanitizeUfoName(name: string): string {
	return name.replace(/_/g, '-');
}

export function sanitizeLabelStackName(name: string): string {
	if (name.startsWith('/') && fg('platform_ufo_trim_labelstack_slashes')) {
		return name.replace(/^\/+/, '');
	}

	return name;
}

export function isSegmentLabel(obj: any): obj is SegmentLabel {
	return obj && typeof obj.name === 'string' && typeof obj.segmentId === 'string';
}

export function buildSegmentTree(labelStacks: LabelStack[]): SegmentTree {
	const r: SegmentItem = { n: 'segment-tree-root', c: {} };
	const config = getConfig();
	const segmentThreshold = config?.segmentsThreshold;
	const addSegmentsMap = new Map<string, number>();

	labelStacks.forEach((labelStack) => {
		const stringifiedLabelStack = stringifyLabelStackWithoutId(labelStack);
		let currentNode = r;

		for (const label of labelStack) {
			const isSegment = isSegmentLabel(label);
			const name = sanitizeLabelStackName(label.name);
			if (isSegment && segmentThreshold && segmentThreshold[name]) {
				const threshold = segmentThreshold[name];
				const count = addSegmentsMap.get(stringifiedLabelStack) || 0;

				if (count < threshold) {
					addSegmentsMap.set(stringifiedLabelStack, count + 1);
				} else {
					break;
				}
			}
			const id = isSegment ? label.segmentId : undefined;
			const key = id !== undefined ? id : name;
			const type = isSegment ? label.type : undefined;
			if (!currentNode.c) {
				currentNode.c = {};
			}
			if (!currentNode.c[key]) {
				currentNode.c[key] = {
					n: name,
					...(type ? { t: type } : {}),
				};
			}
			currentNode = currentNode.c[key];
		}
	});

	return { r };
}

export function stringifyLabelStackFully(labelStack: LabelStack): string {
	return labelStack
		.map((l) => {
			const name = sanitizeLabelStackName(l.name);
			if (isSegmentLabel(l)) {
				return `${name}:${l.segmentId}`;
			}
			return name;
		})
		.join('/');
}

export function stringifyLabelStackWithoutId(labelStack: LabelStack): string {
	return labelStack
		.map((l) => {
			const name = sanitizeLabelStackName(l.name);
			if (isSegmentLabel(l)) {
				return `${name}:segment`;
			}
			return name;
		})
		.join('/');
}

function getLabelStackReference(labelStack: LabelStack): string {
	return labelStack
		.map((l) => (isSegmentLabel(l) ? l.segmentId : sanitizeLabelStackName(l.name)))
		.join('/');
}

export function labelStackStartWith(labelStack: LabelStack, startWith: LabelStack): boolean {
	return stringifyLabelStackFully(labelStack).startsWith(stringifyLabelStackFully(startWith));
}

export function optimizeLabelStack(
	labelStack: LabelStack,
	reactUFOVersion: ReturnType<typeof getReactUFOPayloadVersion>,
):
	| string
	| {
			t?: UFOSegmentType | undefined;
			s?: string | undefined;
			n: string;
	  }[] {
	return reactUFOVersion === '2.0.0'
		? getLabelStackReference(labelStack)
		: labelStack.map((ls) => ({
				n: sanitizeLabelStackName(ls.name),
				...((ls as SegmentLabel).segmentId ? { s: (ls as SegmentLabel).segmentId } : {}),
				...((ls as SegmentLabel).type ? { t: (ls as SegmentLabel).type } : {}),
			}));
}

/**
 * Optimizes a labelStack with registry-based deduplication.
 * When a registry is provided and the version is '2.0.0', the labelStack string
 * is registered in the lookup table and a numeric index is returned instead of the full string.
 * Falls back to regular optimizeLabelStack when no registry is provided.
 */
export function optimizeLabelStackWithRegistry(
	labelStack: LabelStack,
	reactUFOVersion: ReturnType<typeof getReactUFOPayloadVersion>,
	registry: LabelStackRegistry | undefined,
):
	| string
	| number
	| {
			t?: UFOSegmentType | undefined;
			s?: string | undefined;
			n: string;
	  }[] {
	if (registry && reactUFOVersion === '2.0.0') {
		const ref = getLabelStackReference(labelStack);
		return registry.register(ref);
	}
	return optimizeLabelStack(labelStack, reactUFOVersion);
}

export function getOldSegmentsLabelStack(
	segments: SegmentInfo[],
	_interactionType: InteractionType,
):
	| {
			labelStack: any[];
	  }[]
	| {
			labelStack:
				| string
				| {
						t?: UFOSegmentType | undefined;
						s?: string | undefined;
						n: string;
				  }[];
	  }[] {
	const config = getConfig();
	const addSegmentsMap = new Map<string, number>();
	const segmentThreshold = config?.segmentsThreshold;
	return segments.map(({ labelStack, ...others }) => {
		const stringifiedLabelStack = stringifyLabelStackWithoutId(labelStack);
		const segmentsInfo: any[] = [];
		for (const ls of labelStack) {
			const isSegment = isSegmentLabel(ls);
			const name = sanitizeLabelStackName(ls.name);
			if (isSegment && segmentThreshold && segmentThreshold[name]) {
				const threshold = segmentThreshold[name];
				const count = addSegmentsMap.get(stringifiedLabelStack) || 0;
				if (count < threshold) {
					addSegmentsMap.set(stringifiedLabelStack, count + 1);
				} else {
					break;
				}
			}
			segmentsInfo.push({
				n: name,
				...((ls as SegmentLabel).segmentId ? { s: (ls as SegmentLabel).segmentId } : {}),
				...((ls as SegmentLabel).type ? { t: (ls as SegmentLabel).type } : {}),
			});
		}
		return { ...others, labelStack: segmentsInfo };
	});
}
